import { Fragment, useCallback, useMemo, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PanelMenu } from 'primereact/panelmenu'
import type { MenuItem } from 'primereact/menuitem'
import { Tooltip } from 'primereact/tooltip'
import { MODULES, type MenuSubItem } from './menu'
import { useActiveMenu } from './useActiveMenu'
import { useCurrentUser, useGrants } from '../../auth/useCurrentUser'

interface NavMenuProps {
  /** Bir öğeye tıklanınca (ör. mobil drawer'ı kapatmak için). */
  onNavigate?: () => void
  /** Daraltılmış (ikon-ray) mod — yalnız modül ikonları + tooltip; alt öğeler gizli. */
  collapsed?: boolean
}

/**
 * Sol menü — Figma: açık zeminli sidebar; aktif modül açık (alt öğeler görünür), diğerleri chevron ile kapalı.
 * Açık modda PrimeReact **PanelMenu** (accordion) kullanılır; daraltılmış modda ikon-ray + Tooltip.
 * Menü verisi tek kaynaktan (`menu.ts`); yönlendirme react-router `navigate` ile yapılır.
 */
export default function NavMenu({ onNavigate, collapsed = false }: Readonly<NavMenuProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const active = useActiveMenu()
  const { hasAnyGrant } = useGrants()
  const { data: me } = useCurrentUser()
  const roleIds = me?.roleIds
  // Stabil primitifler — useMemo/derive bağımlılıkları için (active nesnesi her render'da yeni).
  const activeModuleKey = active?.module.key
  const activeItemPath = active?.item?.path

  // Menü görünürlüğü (FR-016): kullanıcının efektif yetkisine göre öğeleri süz; hiç görünür öğesi
  // kalmayan modül gizlenir. viewGrants tanımsız öğe her zaman görünür. Ek olarak, {@code hideForRoleIds}
  // içeren öğe kullanıcının rol KİMLİKLERİNDEN biriyle eşleşirse gizlenir (grant ile gizlenemeyen kozmetik
  // istisnalar için; rol adı DEĞİL — ad değişebilir, bkz. ROLE_IDS). Grup öğelerinde (children) çocuklar
  // da tek tek süzülür; hiç görünür çocuğu kalmayan grup gizlenir. Asıl koruma backend'de.
  const visibleModules = useMemo(() => {
    const roleIdSet = roleIds ?? []
    const canView = (item: MenuSubItem) =>
      (!item.viewGrants || hasAnyGrant(...item.viewGrants)) &&
      !item.hideForRoleIds?.some((id) => roleIdSet.includes(id))
    return MODULES.map((mod) => ({
      ...mod,
      items: mod.items
        .map((item) =>
          item.children ? { ...item, children: item.children.filter(canView) } : item,
        )
        .filter((item) => (item.children ? item.children.length > 0 : canView(item))),
    })).filter((mod) => mod.items.length > 0)
  }, [hasAnyGrant, roleIds])

  // Yaprak öğe → command (gezinir). Model kurucusundan AYRI durur: hepsi tek useMemo içinde iç içe
  // tanımlıysa map zinciri 4 seviyeden fazla derinleşiyordu (Sonar typescript:S2004) ve düğüm türleri
  // okunmuyordu.
  const leaf = useCallback(
    (modKey: string, item: MenuSubItem) => ({
      key: `${modKey}:${item.path}`,
      label: t(item.labelKey),
      className: activeItemPath === item.path ? 'nav-sub-on' : undefined,
      command: () => {
        onNavigate?.()
        if (item.path) navigate(item.path)
      },
    }),
    [t, activeItemPath, onNavigate, navigate],
  )

  /**
   * Grup öğe (children) → yalnız açılıp alt öğeleri gösteren düğüm; aksi halde doğrudan yaprak.
   *
   * <p><b>Tek görünür çocuğu kalan grup, YALNIZ {@code flattenWhenSingle} işaretliyse DÜZLEŞİR</b>
   * (28.09.2026): açılır ok çizilmez, öğe doğrudan o sayfaya gider — ama etiketi <b>grubun</b>
   * adıdır, çocuğun değil.
   *
   * <p><b>Neden tüm gruplara uygulanmıyor:</b> grup adları çoğunlukla jeneriktir ("Parametreler",
   * "Görevler"); genel bir kural, tek yetkili kullanıcıya nereye gittiğini söylemeyen bir satır
   * gösterirdi (bağımsız code-review bulgusu, 28.09.2026 — ilk yazımda genel kuraldı ve geri
   * alındı). Bayrağın gerekçesi {@code MenuSubItem.flattenWhenSingle} javadoc'unda.
   */
  const node = useCallback(
    (modKey: string, item: MenuSubItem) => {
      if (!item.children) {
        return leaf(modKey, item)
      }
      if (item.flattenWhenSingle && item.children.length === 1) {
        // key/aktiflik çocuktan (rota ona ait), görünen ad gruptan.
        return { ...leaf(modKey, item.children[0]), label: t(item.labelKey) }
      }
      return {
        key: `${modKey}:${item.labelKey}`,
        label: t(item.labelKey),
        // Grup satırı (aç/kapa oklu): ok metnin solunda durduğundan etiket yaprak
        // kardeşlerinden sağa kayar; hizayı CSS'te bu sınıfla düzeltiriz (AppLayout.css).
        className: 'nav-group',
        items: item.children.map((child) => leaf(modKey, child)),
      }
    },
    [t, leaf],
  )

  // PanelMenu modeli — aktif alt öğe/modül vurgusu className ile verilir (route değişince yeniden hesaplanır).
  const model = useMemo<MenuItem[]>(() => {
    const out: MenuItem[] = []
    for (const mod of visibleModules) {
      // `flat` modülün BAŞLIĞI çizilmez: öğeleri kök seviyeye açılır (kullanıcı isteği 2026-09-18).
      // Kendi grupları (children) korunur — kalkan yalnız modül satırıdır.
      if (mod.flat) {
        // İKON kök seviyede ŞART: modül başlıkları ikonlu çizildiği için ikonsuz öğeler onların
        // yanında sola kayık duruyordu (kullanıcı bildirimi 2026-09-18). İkon yalnız BURADA, kök
        // seviyeye çıkan öğelere eklenir — `leaf`/`node` içine konsaydı diğer modüllerin açılır
        // alt öğeleri de ikon alır ve mevcut görünüm değişirdi.
        out.push(...mod.items.map((item) => ({ ...node(mod.key, item), icon: item.icon })))
        continue
      }
      // Değişkene alınır, doğrudan push'lanmaz: PrimeReact'in `MenuItem` tipinde `key` yok ve
      // nesne değişmezi doğrudan itilirse fazlalık-alan denetimi derlemeyi kırar.
      const moduleNode = {
        key: mod.key,
        label: t(mod.labelKey),
        icon: mod.icon,
        className: activeModuleKey === mod.key ? 'nav-mod-on' : undefined,
        // Modül başlığı yalnız aç/kapa yapar; gezinme yok (yalnız alt öğeye tıklanınca gidilir).
        items: mod.items.map((item) => node(mod.key, item)),
      }
      out.push(moduleNode)
    }
    return out
  }, [t, activeModuleKey, visibleModules, node])

  // Açık modüller tamamen kullanıcı denetiminde: her başlık serbestçe açılıp kapanır (tekrar
  // tıklayınca kapanır), birden çok modül açık kalabilir ve **hepsi kapalı olabilir** (zorunlu açık yok).
  // Yalnız ilk açılışta (mount) **aktif rotanın** modülü açık gelir; aktif modül yoksa (ör. Anasayfa)
  // hiçbir modül açılmaz — giriş sonrası menü tamamen kapalı gelir (varsayılan modül açma yok).
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>(() => {
    if (!active) return {}
    const keys: Record<string, boolean> = { [active.module.key]: true }
    // Aktif yaprak bir grubun (children) altındaysa o grubu da aç → grup altındaki bir ekrana deep-link ile
    // gelindiğinde (ör. /tickets/ticket-types → Parametreler) öğe menüde görünür ve vurgulu gelir.
    const activePath = active.item?.path
    if (activePath) {
      const group = active.module.items.find((it) =>
        it.children?.some((c) => c.path === activePath),
      )
      if (group) keys[`${active.module.key}:${group.labelKey}`] = true
    }
    return keys
  })

  // Daraltılmış: modül ikonları + açık modüllerin alt öğe ikonları (Figma "Side Menu kapalı").
  // Alt öğe açık/kapalı durumu açık menüyle **aynı** state'ten (expandedKeys) gelir → iki mod senkron.
  // Her ikon hover'da adını tooltip ile gösterir (FR-013); aktif alt öğe accent ile vurgulanır.
  if (collapsed) {
    /** Bir yaprak öğe → ikon bağlantısı. `child` yalnız görsel kademelendirme içindir. */
    const subIcon = (item: MenuSubItem, i: number, child = false) => {
      const itemLabel = t(item.labelKey)
      return (
        <NavLink
          key={item.path}
          to={item.path ?? '#'}
          className={`nav-sub-icon${child ? ' nav-sub-icon--child' : ''}${
            activeItemPath === item.path ? ' on' : ''
          }`}
          onClick={onNavigate}
          aria-label={itemLabel}
          data-pr-tooltip={itemLabel}
          data-pr-position="right"
          // Kademeli (stagger) giriş — sırayla açılıyormuş hissi.
          style={{ animationDelay: `${i * 25}ms` }}
        >
          <i className={`ic ${item.icon}`} aria-hidden="true" />
        </NavLink>
      )
    }

    /**
     * Grup öğesi (children) → açık menüdeki gibi <b>kendi aç/kapa satırı</b>; çocukları yalnız
     * açıkken çizilir.
     *
     * <p><b>Neden (kullanıcı bildirimi 22.09.2026):</b> ray daha önce grupları <i>düzleştiriyordu</i> —
     * grup açık menüde kapalıyken bile çocukları rayda görünüyordu, yani iki mod aynı menüyü
     * göstermiyordu. Aç/kapa durumu açık menüyle <b>aynı anahtardan</b> okunur (modül anahtarı +
     * etiket anahtarı — bkz. `node`), dolayısıyla ikisi artık birbirini izler.
     */
    const groupIcon = (modKey: string, item: MenuSubItem, i: number) => {
      const key = `${modKey}:${item.labelKey}`
      const label = t(item.labelKey)
      const open = !!expandedKeys[key]
      return (
        <Fragment key={key}>
          <button
            type="button"
            className="nav-sub-icon nav-sub-icon--group"
            onClick={() => setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }))}
            aria-label={label}
            aria-expanded={open}
            data-pr-tooltip={label}
            data-pr-position="right"
            style={{ animationDelay: `${i * 25}ms` }}
          >
            <i className={`ic ${item.icon}`} aria-hidden="true" />
            {/* Açılabilirlik göstergesi — modül ikonundakinin küçük kardeşi. */}
            <svg
              className={`nav-sub-caret${open ? ' open' : ''}`}
              viewBox="0 0 12 12"
              aria-hidden="true"
            >
              <path
                d="M3 4.5 6 7.5 9 4.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {/* Çocuklar DOM'a yalnız grup açılınca girdiği için stagger indeksi sıfırdan başlar:
              her açılışta ikonlar sırayla belirir. */}
          {open && item.children?.map((c, j) => subIcon(c, j, true))}
        </Fragment>
      )
    }

    /** Modülün öğeleri → açık menüdeki ağacın aynısı: yapraklar bağlantı, gruplar aç/kapa. */
    const itemIcons = (mod: (typeof visibleModules)[number]) =>
      // `.map(subIcon)` YAZILMAZ (Sonar `typescript:S7727`, BUG): `map` geri çağrıya ÜÇ argüman
      // geçirir (öğe, indeks, dizi) ve üçüncüsü `subIcon`'un `child` parametresine bağlanırdı —
      // her öğe "çocuk" gibi çizilirdi. Çağrı açıkça yazılınca bağ kırılmaz.
      mod.items.map((item, i) => {
        if (!item.children) {
          return subIcon(item, i)
        }
        // Tek görünür çocuk → açık menüdeki `node` ile AYNI indirgeme (aynı opt-in bayrağına bağlı):
        // aç/kapa yerine doğrudan bağlantı; rota çocuktan, ad ve ikon gruptan. İki mod aynı menüyü
        // göstermek zorundadır (22.09.2026 kullanıcı bildirimi), bu yüzden kural burada da geçerli.
        if (item.flattenWhenSingle && item.children.length === 1) {
          return subIcon({ ...item.children[0], labelKey: item.labelKey, icon: item.icon }, i)
        }
        return groupIcon(mod.key, item, i)
      })

    return (
      <nav className="nav nav--collapsed" aria-label={t('nav.modules')}>
        <Tooltip target=".nav-mod-icon, .nav-sub-icon" />
        {visibleModules.map((mod) => {
          const moduleActive = active?.module.key === mod.key
          const modLabel = t(mod.labelKey)
          // Başlıksız modül: aç/kapa düğmesi YOK, ikonlar hep görünür — açık menüde de başlığı yok,
          // dolayısıyla kapatılacak bir şey kalmıyor (kullanıcı isteği 2026-09-18).
          if (mod.flat) {
            return <Fragment key={mod.key}>{itemIcons(mod)}</Fragment>
          }
          return (
            <Fragment key={mod.key}>
              {/* Modül ikonu = aç/kapa (açık menüdeki başlık gibi yalnız toggle; gezinme alt ikonlardan).
                  Aynı expandedKeys'i günceller → iki mod senkron. */}
              <button
                type="button"
                className={`nav-mod-icon${moduleActive ? ' on' : ''}`}
                onClick={() =>
                  setExpandedKeys((prev) => ({ ...prev, [mod.key]: !prev[mod.key] }))
                }
                aria-label={modLabel}
                aria-expanded={!!expandedKeys[mod.key]}
                data-pr-tooltip={modLabel}
                data-pr-position="right"
              >
                <i className={`ic ${mod.icon}`} aria-hidden="true" />
                {/* Açılabilirlik göstergesi: kapalıyken aşağı, açıkken yukarı bakan küçük ok. */}
                <svg
                  className={`nav-mod-caret${expandedKeys[mod.key] ? ' open' : ''}`}
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                >
                  <path
                    d="M3 4.5 6 7.5 9 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {/* Açık modüllerin altları ikon olarak gösterilir (açık menüdeki accordion ile senkron). */}
              {expandedKeys[mod.key] && itemIcons(mod)}
            </Fragment>
          )
        })}
      </nav>
    )
  }

  return (
    <>
      {/* Figma sidebar: modül listesi üstünde "MODÜLLER" overline başlığı. */}
      <div className="nav-overline">{t('nav.modules')}</div>
      <PanelMenu
        model={model}
        expandedKeys={expandedKeys}
        onExpandedKeysChange={(keys) => setExpandedKeys(keys as Record<string, boolean>)}
        multiple
        className="nav sidebar-menu"
        aria-label={t('nav.modules')}
      />
    </>
  )
}
